import './generate-image.css'
import { Component, createEffect, createSignal, onMount, onCleanup, Show } from 'solid-js'
import GenerateImageBodyOptions from '/web/shared/aifans/GenerateImage/GenerateImageBodyOptions'
import GenerateImageActionOptions from '/web/shared/aifans/GenerateImage/GenerateImageActionOptions'
import GenerateImageClothingOptions from '/web/shared/aifans/GenerateImage/GenerateImageClothingOptions'
import GenerateImageAccessoriesOptions from '/web/shared/aifans/GenerateImage/GenerateImageAccessoriesOptions'
import GenerateImageViewOptions from '/web/shared/aifans/GenerateImage/GenerateImageViewOptions'
import { useNavigate, useParams } from '@solidjs/router'
import { AppSchema } from '/common/types'
import { getAssetUrl } from '/web/shared/util'
import { characterStore, toastStore, userStore } from '/web/store'
import { isLoggedIn } from '/web/store/api'
import GenerateImageCharacterModal from '/web/shared/aifans/GenerateImage/GenerateImageCharacterModal'
import { imageApi } from '/web/store/data/image'
import { Link, Meta, Title } from '@solidjs/meta'
import { getCanonicalUrl } from '/web/shared/aifans/lib/config-util'
import InappropriateContentModal from './InappropriateContentModal'
import Footer from '/web/shared/aifans/Footer'
import { TokenCoin } from '/web/shared/aifans/TokenCoin'

const SCENE_PROMPTS: Record<string, string> = {
  'Throne Room': 'wearing royal armor standing in the center of a throne room',
  Bedroom: 'lounging on a bed with soft blankets and warm lighting',
  Rooftop: 'on a rooftop with a glowing city skyline behind them',
  'Cyberpunk City': 'in a futuristic cyberpunk city with holographic billboards and flying cars',
  'Cherry Blossom': 'under falling cherry blossom petals in a Japanese garden',
  'Beach Sunset': 'on a tropical beach at golden hour with waves crashing',
  'Gothic Cathedral': 'inside a gothic cathedral with stained glass and candlelight',
  'Battle Arena': 'in a gladiator battle arena with roaring crowds',
  'Enchanted Lake': 'at the edge of a glowing enchanted lake surrounded by mist',
  'Crystal Cave': 'inside a crystal cave with glowing purple and blue gems',
  'Samurai Temple': 'in front of an ancient samurai temple at dawn',
  'Space Station': 'aboard a space station with Earth visible through the windows',
}

const TABS = [
  { id: 0, label: 'Action' },
  { id: 1, label: 'Body' },
  { id: 2, label: 'Clothing' },
  { id: 3, label: 'Accessories' },
  { id: 4, label: 'View' },
]

const DEFAULT_PROMPT = ''
const PROMPT_STORAGE_KEY = 'generate-image-prompt'

const GenerateImage: Component = () => {
  const params = useParams()
  const nav = useNavigate()

  const getInitialPrompt = () => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(PROMPT_STORAGE_KEY)
      return saved || DEFAULT_PROMPT
    }
    return DEFAULT_PROMPT
  }

  const [prompt, setPrompt] = createSignal(getInitialPrompt())
  const [character, setCharacter] = createSignal<AppSchema.Character | undefined>(undefined)
  const [showCharacterPicker, setShowCharacterPicker] = createSignal(false)
  const [characters, setCharacters] = createSignal<AppSchema.Character[]>([])
  const [activeTab, setActiveTab] = createSignal(0)
  const [scene, setScene] = createSignal<string | null>(null)
  const [image, setImage] = createSignal<string | undefined>(undefined)
  const [generating, setGenerating] = createSignal(false)
  const [showInappropriateContentModal, setShowInappropriateContentModal] = createSignal(false)
  const [inappropriateContent, setInappropriateContent] = createSignal<string | undefined>(
    undefined
  )

  const chars = characterStore((s) => ({
    list: s.characters.list,
    map: s.characters.list.reduce<Record<string, AppSchema.Character>>(
      (prev, curr) => Object.assign(prev, { [curr._id]: curr }),
      {}
    ),
    loaded: s.characters.loaded,
  }))

  // Gate any meaningful action behind the login modal when logged out.
  const requireLogin = () => {
    if (!isLoggedIn()) {
      userStore.loginModal(true)
      return true
    }
    return false
  }

  const updatePrompt = (value: string, replacePrompt: boolean) => {
    if (requireLogin()) return
    if (replacePrompt) {
      setPrompt(value)
      if (typeof window !== 'undefined') {
        localStorage.setItem(PROMPT_STORAGE_KEY, value)
      }
    } else {
      let p: string = prompt()
      const trimmedValue = value.trim()

      if (p.includes(trimmedValue)) {
        p = p.replace(`, ${trimmedValue}`, '')
        p = p.replace(`${trimmedValue}, `, '')
        p = p.replace(trimmedValue, '')
        p = p.replace(/,,+/g, ',')
        p = p.replace(/^,\s*/, '')
        p = p.replace(/,\s*$/, '')
        setPrompt(p)
        if (typeof window !== 'undefined') {
          localStorage.setItem(PROMPT_STORAGE_KEY, p)
        }
      } else {
        if (p && !p.endsWith(',')) {
          p = `${p}, `
        }
        const newPrompt = `${p}${trimmedValue}`
        setPrompt(newPrompt)
        if (typeof window !== 'undefined') {
          localStorage.setItem(PROMPT_STORAGE_KEY, newPrompt)
        }
      }
    }
  }

  const selectScene = (name: string) => {
    if (requireLogin()) return
    if (scene() === name) {
      setScene(null)
      setPrompt('')
      if (typeof window !== 'undefined') localStorage.setItem(PROMPT_STORAGE_KEY, '')
    } else {
      setScene(name)
      const val = SCENE_PROMPTS[name]
      setPrompt(val)
      if (typeof window !== 'undefined') localStorage.setItem(PROMPT_STORAGE_KEY, val)
    }
  }

  const clearAll = () => {
    setScene(null)
    setPrompt('')
    if (typeof window !== 'undefined') localStorage.removeItem(PROMPT_STORAGE_KEY)
  }

  const generateImage = async () => {
    if (requireLogin()) return
    const charId = character()?._id

    if (!charId) {
      setShowCharacterPicker(true)
      return
    }

    setGenerating(true)
    setImage(undefined)
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })

    if (charId) {
      try {
        const res = await imageApi.generateFanImage(prompt(), 'summary', charId)

        if (res.error) {
          if (res.error.includes('Insufficient tokens')) {
            userStore.showNoTokensModal(true)
          } else if (res.error.includes('contains inappropriate content')) {
            setInappropriateContent(res.error)
            setShowInappropriateContentModal(true)
          } else {
            toastStore.error(`Failed to request image: ${res.error}`)
          }
        } else {
          const url = res?.result?.output
          const img = url ? getAssetUrl(url, true) : ''
          if (userStore().user?.admin && res?.result?.info) {
            console.log(JSON.stringify(JSON.parse(res?.result?.info), null, 2))
          }
          img && setImage(img)
        }
      } catch (error: any) {
        if (error.message.includes('Insufficient tokens')) {
          userStore.showNoTokensModal(true)
        } else if (error.message.includes('contains inappropriate content')) {
          setInappropriateContent(error.message)
          setShowInappropriateContentModal(true)
        } else {
          toastStore.error(`Failed to request image: ${error.message}`)
        }
      } finally {
        setGenerating(false)
      }
    }
  }

  createEffect(() => {
    if (params.id) {
      const char = chars.list.find((c) => c._id === params.id)
      setCharacter(char)
    }
    setCharacters(chars.list)
  })

  onMount(async () => {
    // Character list is only needed for the (login-gated) picker.
    if (isLoggedIn() && !chars.loaded) {
      await characterStore.getCharacters()
    }
    setCharacters(chars.list)
  })

  onCleanup(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(PROMPT_STORAGE_KEY)
    }
  })

  return (
    <>
      <Title>CosplayFans - Generate Cosplay Images</Title>
      <Meta name="og:title" content="CosplayFans - Generate Cosplay Images" />
      <Meta
        name="description"
        content="Create stunning AI-generated cosplay images on CosplayFans.ai. Choose your style, character, and more with our powerful AI image generator exclusives."
      />
      <Meta
        name="og:description"
        content="Create stunning AI-generated cosplay images on CosplayFans.ai. Choose your style, character, and more with our powerful AI image generator exclusives."
      />
      <Meta
        name="og:image"
        content="https://cdn.cosplayfanscdn.com/assets/char-7abb4c7b-f0ff-4f56-aeca-9ed8ba655811.png"
      />
      <Link rel="canonical" href={getCanonicalUrl('/generate-image/')} />

      <section style={{ padding: '20px clamp(20px, 3vw, 56px) 32px' }}>
        {/* ── Header ── */}
        <div class="mb-[14px] flex flex-shrink-0 flex-wrap items-end justify-between gap-6">
          <div>
            <div
              class="mb-[10px] inline-flex items-center gap-2 rounded-full px-[14px] py-[6px] font-rajdhani text-[11px] font-bold uppercase tracking-[1.2px] text-[#FF3CAC]"
              style={{
                background: 'rgba(255,60,172,0.12)',
                border: '1px solid rgba(255,60,172,0.3)',
              }}
            >
              <span class="h-[6px] w-[6px] rounded-full bg-[var(--cf-cyan)] shadow-[0_0_6px_var(--cf-cyan)]" />
              AI Image Studio
            </div>
            <h1
              class="font-bebas leading-[0.95] text-white"
              style={{ 'font-size': 'clamp(28px, 3.4vw, 48px)' }}
            >
              Generate Image
            </h1>
          </div>
          <div class="inline-flex h-[48px] items-center gap-[10px] rounded-full border border-[rgba(255,209,102,0.3)] bg-[rgba(255,209,102,0.08)] px-[22px] font-rajdhani text-[14px] font-bold uppercase tracking-[0.6px] text-[#ffd166]">
            <TokenCoin size={20} />
            <span>HD Quality</span>
          </div>
        </div>

        {/* ── Grid ── */}
        <div
          class="gen-grid-layout grid items-start gap-5"
          style={{ 'grid-template-columns': '1fr' }}
        >
          {/* ── Preview Column ── */}
          <div class="flex flex-col gap-3">
            <div
              class="gen-preview relative flex w-full items-center justify-center overflow-hidden rounded-[24px]"
              style={{
                background: 'rgba(16,12,30,0.95)',
                border: '1.5px solid rgba(138,43,226,0.5)',
                'min-height': '360px',
                'box-shadow':
                  'inset 0 0 60px rgba(138,43,226,0.12), 0 0 24px rgba(138,43,226,0.15)',
                'aspect-ratio': '3 / 4',
              }}
            >
              <Show when={generating()}>
                <div class="relative text-center">
                  <div
                    class="mx-auto mb-[18px] h-16 w-16 rounded-full"
                    style={{
                      border: '3px solid rgba(138,43,226,0.15)',
                      'border-top-color': 'var(--cf-cyan)',
                      'border-right-color': 'var(--cf-primary)',
                      'box-shadow': '0 0 24px rgba(0,229,255,0.3)',
                      animation: 'genSpin 0.9s linear infinite',
                    }}
                  />
                  <div class="font-bebas text-[22px] tracking-[1px] text-white">
                    Generating your image...
                  </div>
                  <div class="mt-1 font-rajdhani text-[12px] uppercase tracking-[1px] text-[var(--cf-muted)]">
                    This usually takes 5-15 seconds
                  </div>
                </div>
              </Show>

              <Show when={!generating() && image()}>
                <div class="group relative h-full w-full">
                  <img
                    class="h-full w-full object-contain"
                    src={getAssetUrl(image() || '', true)}
                  />
                  <div class="absolute bottom-4 left-4 right-4 flex flex-wrap justify-center gap-2 opacity-0 transition-opacity duration-[250ms] group-hover:opacity-100">
                    <button class="h-9 rounded-full border border-[rgba(255,255,255,0.18)] bg-[rgba(8,6,16,0.85)] px-[14px] font-rajdhani text-[12px] font-bold tracking-[0.6px] text-white backdrop-blur-[10px] transition-all duration-150 hover:border-[rgba(0,229,255,0.4)] hover:bg-[rgba(0,229,255,0.18)]">
                      ⬇ Download
                    </button>
                    <button class="h-9 rounded-full border border-[rgba(255,255,255,0.18)] bg-[rgba(8,6,16,0.85)] px-[14px] font-rajdhani text-[12px] font-bold tracking-[0.6px] text-white backdrop-blur-[10px] transition-all duration-150 hover:border-[rgba(0,229,255,0.4)] hover:bg-[rgba(0,229,255,0.18)]">
                      ♥ Save
                    </button>
                  </div>
                </div>
              </Show>

              <Show when={!generating() && !image()}>
                <div class="relative p-8 text-center">
                  <div
                    class="mx-auto mb-[22px] grid h-[128px] w-[128px] place-items-center rounded-[28px] border-2 border-dashed border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.03)] text-[rgba(255,255,255,0.35)]"
                    style={{ animation: 'genIconBob 4s ease-in-out infinite' }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      class="h-16 w-16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M12 3v3M12 18v3M5 12H2M22 12h-3M5.6 5.6 7.8 7.8M16.2 16.2l2.2 2.2M5.6 18.4 7.8 16.2M16.2 7.8l2.2-2.2" />
                      <circle cx="12" cy="12" r="4" />
                    </svg>
                  </div>
                  <div class="mb-[6px] font-bebas text-[32px] tracking-[1.2px] text-[rgba(255,255,255,0.92)]">
                    Your image will appear here
                  </div>
                  <div class="font-nunito text-[15px] text-[var(--cf-muted)]">
                    Pick a character, build your prompt, and hit Generate
                  </div>
                </div>
              </Show>
            </div>
          </div>

          {/* ── Controls Column ── */}
          <div
            class="gen-controls relative flex flex-col gap-[22px] overflow-hidden rounded-[20px] border border-[var(--cf-hairline)] p-[22px]"
            style={{ background: 'rgba(20,16,31,0.65)' }}
          >
            {/* Character */}
            <div class="relative">
              <div class="mb-3 flex items-center justify-between font-rajdhani text-[12px] font-bold uppercase tracking-[1.8px] text-[rgba(255,255,255,0.6)]">
                Character
              </div>
              <button
                class="flex w-full items-center gap-[14px] rounded-[14px] border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.03)] p-[14px] text-left text-white transition-all duration-150 hover:border-[rgba(0,229,255,0.4)] hover:bg-[rgba(0,229,255,0.05)]"
                onClick={() => {
                  if (requireLogin()) return
                  setShowCharacterPicker(true)
                }}
              >
                <Show when={character()}>
                  <img
                    class="h-16 w-16 flex-shrink-0 rounded-[12px] object-cover"
                    src={getAssetUrl(character()!.avatar || '', true, { width: 128 })}
                    alt={character()!.name}
                    style={{ 'object-position': 'center 18%' }}
                  />
                  <div class="min-w-0 flex-1">
                    <div class="font-bebas text-[22px] leading-[1.1] tracking-[0.5px] text-white">
                      {character()!.name}
                    </div>
                    <div class="mt-1 font-rajdhani text-[13px] font-semibold text-[var(--cf-muted)]">
                      by {character()!.realName}
                    </div>
                  </div>
                  <span class="flex-shrink-0 font-rajdhani text-[12px] font-bold uppercase tracking-[0.8px] text-[var(--cf-cyan)]">
                    Change →
                  </span>
                </Show>
                <Show when={!character()}>
                  <div class="grid h-16 w-16 flex-shrink-0 place-items-center rounded-[12px] border-[1.5px] border-dashed border-[rgba(138,43,226,0.4)] bg-[rgba(138,43,226,0.15)] text-[28px] text-[var(--cf-primary)]">
                    +
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="font-bebas text-[22px] leading-[1.1] tracking-[0.5px] text-white">
                      Pick a character
                    </div>
                    <div class="mt-1 font-rajdhani text-[13px] font-semibold text-[var(--cf-muted)]">
                      Required
                    </div>
                  </div>
                </Show>
              </button>
            </div>

            {/* Prompt */}
            <div class="relative">
              <div class="mb-3 flex items-center justify-between font-rajdhani text-[12px] font-bold uppercase tracking-[1.8px] text-[rgba(255,255,255,0.6)]">
                Prompt
                <Show when={prompt()}>
                  <button
                    class="border-none bg-transparent p-0 font-rajdhani text-[11px] font-bold uppercase tracking-[0.8px] text-[var(--cf-cyan)] hover:underline"
                    onClick={clearAll}
                  >
                    Clear
                  </button>
                </Show>
              </div>
              <div class="rounded-[12px] border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.03)] p-[14px_16px] transition-all duration-200 focus-within:border-[rgba(0,229,255,0.45)] focus-within:shadow-[0_0_0_3px_rgba(0,229,255,0.1)]">
                <textarea
                  class="w-full resize-y border-none bg-transparent font-nunito text-[14px] leading-[1.6] text-white outline-none [cursor:default] placeholder:text-[rgba(255,255,255,0.3)]"
                  style={{ 'min-height': '84px', 'max-height': '160px' }}
                  placeholder="Select options below to build your prompt"
                  value={prompt()}
                  readonly
                  aria-readonly="true"
                />
                <div class="mt-2 flex gap-[6px] font-rajdhani text-[10px] font-semibold uppercase tracking-[0.8px] text-[rgba(255,255,255,0.35)]">
                  <span>{prompt().length} chars</span>
                </div>
              </div>
            </div>

            {/* Scene presets */}
            <div class="relative">
              <div class="mb-3 flex items-center justify-between font-rajdhani text-[12px] font-bold uppercase tracking-[1.8px] text-[rgba(255,255,255,0.6)]">
                Scene
                <span class="text-[10px] font-semibold normal-case tracking-[0.6px] text-[rgba(255,255,255,0.3)]">
                  Pick one
                </span>
              </div>
              <div class="flex flex-wrap gap-2">
                {Object.keys(SCENE_PROMPTS).map((s) => (
                  <button
                    class={`inline-flex h-9 items-center gap-[7px] rounded-full px-4 font-rajdhani text-[13px] font-semibold tracking-[0.3px] transition-all duration-150 ${
                      scene() === s
                        ? 'border border-[rgba(138,43,226,0.6)] bg-[rgba(138,43,226,0.3)] text-white shadow-[0_4px_12px_rgba(138,43,226,0.25)]'
                        : 'border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.04)] text-[rgba(255,255,255,0.75)] hover:border-[rgba(138,43,226,0.35)] hover:bg-[rgba(138,43,226,0.15)] hover:text-white'
                    }`}
                    onClick={() => selectScene(s)}
                  >
                    <span
                      class={`h-[6px] w-[6px] flex-shrink-0 rounded-full transition-all duration-150 ${
                        scene() === s
                          ? 'bg-[var(--cf-cyan)] shadow-[0_0_6px_var(--cf-cyan)]'
                          : 'bg-[rgba(255,255,255,0.2)]'
                      }`}
                    />
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Customize tabs */}
            <div class="relative">
              <div class="mb-3 flex gap-[2px] border-b border-[var(--cf-hairline)]">
                {TABS.map((t) => (
                  <button
                    class={`relative inline-flex flex-1 items-center justify-center gap-[7px] border-b-2 px-[10px] py-[11px] font-rajdhani text-[13px] font-bold tracking-[0.6px] transition-all duration-150 ${
                      activeTab() === t.id
                        ? 'border-b-[var(--cf-primary)] text-white'
                        : 'border-b-transparent text-[rgba(255,255,255,0.4)] hover:text-white'
                    }`}
                    onClick={() => setActiveTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div class="pt-1">
                <Show when={activeTab() === 0}>
                  <GenerateImageActionOptions
                    updatePrompt={updatePrompt}
                    currentPrompt={prompt()}
                  />
                </Show>
                <Show when={activeTab() === 1}>
                  <GenerateImageBodyOptions updatePrompt={updatePrompt} currentPrompt={prompt()} />
                </Show>
                <Show when={activeTab() === 2}>
                  <GenerateImageClothingOptions
                    updatePrompt={updatePrompt}
                    currentPrompt={prompt()}
                  />
                </Show>
                <Show when={activeTab() === 3}>
                  <GenerateImageAccessoriesOptions
                    updatePrompt={updatePrompt}
                    currentPrompt={prompt()}
                  />
                </Show>
                <Show when={activeTab() === 4}>
                  <GenerateImageViewOptions updatePrompt={updatePrompt} currentPrompt={prompt()} />
                </Show>
              </div>
            </div>

            {/* Generate CTA */}
            <div class="mt-1">
              <button
                class="gen-cta-btn relative inline-flex h-[62px] w-full items-center justify-center gap-3 overflow-hidden rounded-[16px] border-none font-rajdhani text-[17px] font-extrabold uppercase tracking-[1.4px] text-white transition-all duration-200 hover:translate-y-[-2px] disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  background: 'linear-gradient(135deg, #2dc653, #0d8c3a)',
                  'box-shadow':
                    generating() || !character() || !prompt().trim()
                      ? '0 4px 14px rgba(45,198,83,0.2)'
                      : '0 10px 30px rgba(45,198,83,0.4)',
                  filter:
                    generating() || !character() || !prompt().trim() ? 'saturate(0.7)' : 'none',
                }}
                disabled={generating() || !character() || !prompt().trim()}
                onClick={generateImage}
              >
                <Show when={generating()}>
                  <span
                    class="h-4 w-4 rounded-full border-2 border-[rgba(255,255,255,0.3)]"
                    style={{
                      'border-top-color': '#fff',
                      animation: 'genSpin 0.7s linear infinite',
                    }}
                  />
                  Generating...
                </Show>
                <Show when={!generating()}>
                  <span>★</span>
                  Generate Image
                  <span class="font-rajdhani text-[13px] font-semibold tracking-[0.6px] opacity-80">
                    — 2 tokens
                  </span>
                </Show>
              </button>
              <div class="mt-[10px] text-center font-rajdhani text-[11px] tracking-[0.5px] text-[var(--cf-muted)]">
                Image will be saved to{' '}
                <a
                  href="/gallery"
                  class="font-bold text-[var(--cf-cyan)] no-underline hover:underline"
                  onClick={(e) => {
                    e.preventDefault()
                    if (requireLogin()) return
                    nav('/gallery')
                  }}
                >
                  your gallery
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <GenerateImageCharacterModal
        show={showCharacterPicker()}
        hide={() => setShowCharacterPicker(false)}
        characters={characters()}
        selectedCharacter={character()}
        onSelect={(c) => {
          setCharacter(c)
          setShowCharacterPicker(false)
          nav(`/generate-image/${c._id}`)
        }}
      />

      <InappropriateContentModal
        show={showInappropriateContentModal()}
        onClose={() => setShowInappropriateContentModal(false)}
        content={inappropriateContent()}
      />
    </>
  )
}

export default GenerateImage
